import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { create } from "zustand";

/**
 * Tiny hot-key engine.
 *
 * Why roll our own?  Every off-the-shelf library either pulls in extra
 * runtime weight or assumes a different React tree. We need ~50 lines:
 *
 *   1. A registry mapping a normalised "Mod+K" string to a callback.
 *   2. A single `keydown` listener at window scope.
 *   3. Escape hatches: do nothing while focus is on a text input
 *      *unless* the binding is explicitly marked `allowInInput`.
 *
 * Bindings are normalised so `cmd+k`, `ctrl+k`, `Meta+K` all land on
 * the same key. The platform difference is owned by the consumer
 * (`mod` token resolves at register time).
 */

export interface Hotkey {
  /** Combo string. Tokens: mod, alt, shift, plus a single key (case-insensitive). */
  combo: string;
  /** What to do when fired. */
  handler: (e: KeyboardEvent) => void;
  /** Human label shown in the cheatsheet modal. */
  label: string;
  /** Bucket for the cheatsheet (Navigation, Actions, …). */
  group?: string;
  /** Allow firing while focus is in an input/textarea. Default false. */
  allowInInput?: boolean;
}

type Combo = string;

interface HotkeyState {
  bindings: Map<Combo, Hotkey>;
  register: (h: Hotkey) => () => void;
  list: () => Hotkey[];
}

function normalise(combo: string): string {
  return combo
    .toLowerCase()
    .split("+")
    .map((t) => t.trim())
    .sort((a, b) => priority(a) - priority(b))
    .join("+");
}
function priority(token: string): number {
  if (token === "mod") return 0;
  if (token === "ctrl" || token === "control") return 0;
  if (token === "meta" || token === "cmd") return 0;
  if (token === "alt") return 1;
  if (token === "shift") return 2;
  return 9;
}

export const useHotkeyStore = create<HotkeyState>()((set, get) => ({
  bindings: new Map(),
  register: (h) => {
    const key = normalise(h.combo);
    const next = new Map(get().bindings);
    next.set(key, h);
    set({ bindings: next });
    return () => {
      const m = new Map(get().bindings);
      m.delete(key);
      set({ bindings: m });
    };
  },
  list: () => Array.from(get().bindings.values()),
}));


/**
 * Build a combo string from a keyboard event using the *physical* key
 * (`e.code`) rather than the produced character (`e.key`).
 *
 * Why? When the user has a Korean IME enabled, pressing the F key
 * delivers an event whose `e.key` is "ㄹ" — totally unrecognised by
 * our binding table, so the shortcut silently fails. `e.code` reports
 * the geographically-fixed key (`KeyF`), which makes shortcuts work
 * the same in Korean, Japanese, Dvorak, AZERTY etc.
 */
function eventToCombo(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  if (e.shiftKey) parts.push("shift");

  parts.push(codeToToken(e));
  return normalise(parts.join("+"));
}

/** Map a KeyboardEvent.code to the token used in our binding keys. */
function codeToToken(e: KeyboardEvent): string {
  const c = e.code;
  if (c.startsWith("Key"))   return c.slice(3).toLowerCase();    // KeyF  → f
  if (c.startsWith("Digit")) return c.slice(5);                  // Digit1 → 1
  if (c.startsWith("Numpad")) return c.slice(6).toLowerCase();   // Numpad1 → 1
  switch (c) {
    case "Slash":         return "/";
    case "Backslash":     return "\\";
    case "Comma":         return ",";
    case "Period":        return ".";
    case "Semicolon":     return ";";
    case "Quote":         return "'";
    case "BracketLeft":   return "[";
    case "BracketRight":  return "]";
    case "Minus":         return "-";
    case "Equal":         return "=";
    case "Backquote":     return "`";
    case "Space":         return "space";
    case "Escape":        return "escape";
    case "Enter":         return "enter";
    case "Tab":           return "tab";
    case "Backspace":     return "backspace";
    case "ArrowUp":       return "arrowup";
    case "ArrowDown":     return "arrowdown";
    case "ArrowLeft":     return "arrowleft";
    case "ArrowRight":    return "arrowright";
    default:
      // Last-resort: fall back to e.key (handles unusual layouts).
      return e.key.toLowerCase();
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

/** Register a single hotkey for the lifetime of the calling component. */
export function useHotkey(h: Hotkey): void {
  const register = useHotkeyStore((s) => s.register);
  useEffect(() => register(h), [register, h.combo, h.handler, h.label, h.group, h.allowInInput]);
}

/** Mounted once near the app root; routes the keydown event globally. */
export function useGlobalHotkeys(): void {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useHotkeyStore((s) => s.register);
  const bindings = useHotkeyStore((s) => s.bindings);

  // Default navigation set — registered once. Components may add more.
  useEffect(() => {
    const offs: Array<() => void> = [];
    const nav = t("widgets.hotkeys.group.nav");
    offs.push(register({ combo: "mod+k", label: t("common.search"), group: nav, handler: () => focusSearch(), allowInInput: true }));
    // `?` (Shift+/) — the de-facto "show shortcuts" key (Linear, GitHub,
    // Slack). We avoided ⌘/ because Chrome / macOS / Discord all claim
    // that combo and our event never fires.
    offs.push(register({ combo: "shift+/", label: t("pages.settings.keyboard.shortcutList"), group: t("widgets.hotkeys.group.help"), handler: () => window.dispatchEvent(new Event("expertgas:cheatsheet")) }));
    offs.push(register({ combo: "mod+1", label: t("pages.settings.keyboard.goDashboard"), group: nav, handler: () => navigate("/") }));
    offs.push(register({ combo: "mod+2", label: t("pages.settings.keyboard.goSites"),     group: nav, handler: () => navigate("/sites") }));
    offs.push(register({ combo: "mod+3", label: t("pages.settings.keyboard.goSensors"),   group: nav, handler: () => navigate("/sensors") }));
    offs.push(register({ combo: "mod+4", label: t("pages.settings.keyboard.goAlerts"),    group: nav, handler: () => navigate("/alerts") }));
    offs.push(register({ combo: "mod+,", label: t("pages.settings.keyboard.goSettings"),  group: nav, handler: () => navigate("/settings") }));
    return () => offs.forEach((off) => off());
  }, [navigate, register, t]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const combo = eventToCombo(e);
      const binding = bindings.get(combo);
      if (!binding) return;
      if (!binding.allowInInput && isEditableTarget(e.target)) return;
      e.preventDefault();
      binding.handler(e);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bindings]);
}

function focusSearch(): void {
  const el = document.querySelector<HTMLInputElement>("header input[type='search']");
  if (el) {
    el.focus();
    el.select();
  }
}

